import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useT, useLang } from "../../i18n";
import { PAGES, pageUrl, seoText } from "../../seo";
import { useShop, PhoneCard, CallButton } from "../../component/shop";

const memRank = (m) => parseInt(m, 10) * (m.includes("TB") ? 1024 : 1);
const EMPTY = { brand: "", model: "", mem: "", color: "", min: "", max: "" };

const Catalog = ({ pageKey = "catalog" }) => {
    const t = useT();
    const { lang } = useLang();
    const preset = PAGES[pageKey]?.brand; // /iphone, /samsung, /xiaomi — только эта марка
    const seo = seoText(lang, pageKey);
    const shop = useShop();
    const [f, setF] = useState(EMPTY);
    const [q, setQ] = useState("");
    const [sort, setSort] = useState("new");
    const [open, setOpen] = useState(false);
    const set = (p) => setF((x) => ({ ...x, ...p }));
    const phones = shop.phones || [];

    useEffect(() => { window.scrollTo(0, 0); }, []);
    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [open]);

    // Фильтр; except — какой фильтр не учитывать (чтобы показать доступные варианты)
    const filtered = (except) => {
        const nq = q.trim().toLowerCase();
        return phones.filter((p) =>
            (!preset || preset.test(`${p.brand} ${p.model}`)) &&
            (except === "brand" || !f.brand || p.brand === f.brand) &&
            (except === "model" || !f.model || p.model === f.model) &&
            (except === "mem" || !f.mem || p.memory === f.mem) &&
            (except === "color" || !f.color || p.color === f.color) &&
            (!f.min || p.price >= Number(f.min)) && (!f.max || p.price <= Number(f.max)) &&
            (!nq || `${p.brand} ${p.model} ${p.memory} ${p.color}`.toLowerCase().includes(nq)));
    };
    const uniq = (a) => [...new Set(a)];
    const brands = uniq(phones.filter((p) => !preset || preset.test(`${p.brand} ${p.model}`)).map((p) => p.brand)).sort();
    const models = uniq(filtered("model").filter((p) => !f.brand || p.brand === f.brand).map((p) => p.model)).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    const mems = uniq(filtered("mem").map((p) => p.memory)).sort((a, b) => memRank(a) - memRank(b));
    const colors = uniq(filtered("color").map((p) => p.color)).sort();

    const list = useMemo(() => {
        const L = filtered();
        const by = { cheap: (a, b) => a.price - b.price, exp: (a, b) => b.price - a.price, bat: (a, b) => (b.battery ?? 0) - (a.battery ?? 0) }[sort];
        return by ? L.sort(by) : L; // «Сначала новые» — порядок сервера
    }, [phones, f, q, sort]); // eslint-disable-line react-hooks/exhaustive-deps
    const active = Object.values(f).filter(Boolean).length;

    const Chips = ({ k, items }) => (
        <div className="chips">
            <button className={`chip ${!f[k] ? "on" : ""}`} onClick={() => set({ [k]: "", ...(k === "brand" ? { model: "" } : {}) })}>{t("f_all")}</button>
            {items.map((v) => <button key={v} className={`chip ${f[k] === v ? "on" : ""}`} onClick={() => set({ [k]: v, ...(k === "brand" ? { model: "" } : {}) })}>{v}</button>)}
        </div>
    );

    return (
        <main>
            <div className="wrap cat-top"><h1>{seo.h1}</h1><p>{preset ? seo.desc : t("cat_p")}</p>
                {preset && <p><Link to={pageUrl(lang, "catalog")} className="cat-all">← {t("cta_all")}</Link></p>}</div>
            <div className="wrap cat">
                <aside className={`filters ${open ? "open" : ""}`} aria-label={t("filters")}>
                    <div className="fgrp"><h4>{t("f_brand")}</h4><Chips k="brand" items={brands} /></div>
                    <div className="fgrp"><h4>{t("f_model")}</h4>
                        <select value={f.model} onChange={(e) => { const m = e.target.value; set({ model: m, brand: m ? phones.find((p) => p.model === m).brand : f.brand }); }}>
                            <option value="">{t("f_any")}</option>{models.map((m) => <option key={m}>{m}</option>)}
                        </select></div>
                    <div className="fgrp"><h4>{t("f_mem")}</h4><Chips k="mem" items={mems} /></div>
                    <div className="fgrp"><h4>{t("f_color")}</h4>
                        <select value={f.color} onChange={(e) => set({ color: e.target.value })}>
                            <option value="">{t("f_any")}</option>{colors.map((c) => <option key={c}>{c}</option>)}
                        </select></div>
                    <div className="fgrp"><h4>{t("f_price")}</h4>
                        <div className="price2">
                            <input type="number" inputMode="numeric" step="100000" placeholder={t("f_from")} value={f.min} onChange={(e) => set({ min: e.target.value })} aria-label={t("f_from")} />
                            <input type="number" inputMode="numeric" step="100000" placeholder={t("f_to")} value={f.max} onChange={(e) => set({ max: e.target.value })} aria-label={t("f_to")} />
                        </div></div>
                    <button className="fclear" onClick={() => { setF(EMPTY); setQ(""); }}>{t("f_reset")}</button>
                    <button className="btn fbtn" onClick={() => setOpen(false)}>{t("f_show")} ({list.length})</button>
                </aside>
                <div>
                    <div className="cbar">
                        <div className="q"><svg viewBox="0 0 24 24"><path d="M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM21 21l-5-5" /></svg>
                            <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} aria-label={t("search")} /></div>
                        <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="sort">
                            {[["new", "sort_new"], ["cheap", "sort_cheap"], ["exp", "sort_exp"], ["bat", "sort_bat"]].map(([v, k]) => <option key={v} value={v}>{t(k)}</option>)}
                        </select>
                        <button className="btn ghost fbtn" onClick={() => setOpen(true)}><svg viewBox="0 0 24 24"><path d="M4 6h16M7 12h10M10 18h4" /></svg>{t("filters")}{active ? ` · ${active}` : ""}</button>
                        <span className="cnt">{t("found", { n: list.length })}</span>
                    </div>
                    {shop.error ? <div className="empty"><b>{t("load_err")}</b></div>
                        : !shop.phones ? <div className="empty">{t("loading")}</div>
                        : list.length ? <div className="grid">{list.map((p) => <PhoneCard key={p.id} p={p} />)}</div>
                        : <div className="empty"><b>{t("empty_h")}</b>{t("empty_p")}<div style={{ marginTop: 16 }}><CallButton /></div></div>}
                </div>
            </div>
            {open && <div className="fscrim" onClick={() => setOpen(false)} />}
        </main>
    );
};

export default Catalog;
